import { ReactElement, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { projectPointOntoWallAxisCartesianAware } from '@geometry'
import { Stack, Typography } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { CoordinatePointInput, Select } from '@ui/forms'

interface Props {
  wall: ShapeObject
  isPlacementEditable: boolean
  /** Disable all inputs, e.g. while the wall geometry is being saved. */
  disabled?: boolean
}

const FormFields = ({ wall, isPlacementEditable, disabled = false }: Props): ReactElement => {
  const { setValue, getValues } = useFormContext()

  const handleStartReplace = useCallback(
    (x: number, y: number, z: number) => {
      const { start } = getValues()
      const point = new ImmutableVector3(x, y, start?.z ?? z)
      const projected = projectPointOntoWallAxisCartesianAware(wall, point)

      setValue('start', {
        x: projected.x,
        y: projected.y,
        z: projected.z,
      })
    },
    [getValues, setValue, wall],
  )

  const handleStartXChange = useCallback(
    (x: number | null) => {
      if (x === null) return
      const { start } = getValues()
      handleStartReplace(x, start.y, start.z)
    },
    [getValues, handleStartReplace],
  )

  const handleStartYChange = useCallback(
    (y: number | null) => {
      if (y === null) return
      const { start } = getValues()
      handleStartReplace(start.x, y, start.z)
    },
    [getValues, handleStartReplace],
  )

  const handleEndReplace = useCallback(
    (x: number, y: number, z: number) => {
      const { end } = getValues()
      const point = new ImmutableVector3(x, y, end?.z ?? z)
      const projected = projectPointOntoWallAxisCartesianAware(wall, point)

      setValue('end', {
        x: projected.x,
        y: projected.y,
        z: projected.z,
      })
    },
    [getValues, setValue, wall],
  )

  const handleEndXChange = useCallback(
    (x: number | null) => {
      if (x === null) return
      const { end } = getValues()
      handleEndReplace(x, end.y, end.z)
    },
    [getValues, handleEndReplace],
  )

  const handleEndYChange = useCallback(
    (y: number | null) => {
      if (y === null) return
      const { end } = getValues()
      handleEndReplace(end.x, y, end.z)
    },
    [getValues, handleEndReplace],
  )

  return (
    <Stack spacing={1}>
      <Stack direction="column" spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize="inherit" color="red">
            Start
          </Typography>
          <CoordinatePointInput
            label="Start"
            name="start"
            onChangeX={handleStartXChange}
            onChangeY={handleStartYChange}
            onReplace={handleStartReplace}
            pointManipulation={{ x: { disabled }, y: { disabled }, z: { disabled: true } }}
            disableReplace={disabled}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Typography fontSize="inherit">Ende</Typography>
          <CoordinatePointInput
            label="Ende"
            name="end"
            onChangeX={handleEndXChange}
            onChangeY={handleEndYChange}
            onReplace={handleEndReplace}
            pointManipulation={{ x: { disabled }, y: { disabled }, z: { disabled: true } }}
            disableReplace={disabled}
          />
        </Stack>
      </Stack>

      <Select
        label="Platzierung auswählen"
        name="placement"
        options={[
          {
            value: 'External',
            label: 'Extern',
          },
          {
            value: 'Internal',
            label: 'Intern',
          },
        ]}
        disabled={disabled || !isPlacementEditable}
        data-cy="select-placement"
      />
    </Stack>
  )
}

export default FormFields
